import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { Video } from '../types/video';
import BodyPortal from './BodyPortal';

interface WbVideoItem {
  name: string;
  url: string;
  createdAt: string;
  fileSize?: string;
  alreadyImported: boolean;
}

interface WBCollectionsModalProps {
  onClose: () => void;
  onImported: (video: Video) => void;
}

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

/** Whiteboard names files by a random id (e.g. "55cfa039…f0e.webm"); show a readable name then. */
const wbDisplayName = (name: string, createdAt: string, fallback: string) => {
  const base = String(name || '').replace(/\.[^.]+$/, '').trim();
  return !base || /^[0-9a-f-]{16,}$/i.test(base) ? `${fallback} · ${formatDate(createdAt)}` : base;
};

/** Whiteboard stores the size in GB (e.g. "0.0082"). */
const formatWbSize = (gb?: string) => {
  const bytes = Number(gb) * 1024 * 1024 * 1024;
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  return bytes >= 1024 * 1024 * 1024
    ? ` · ${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
    : ` · ${Math.max(0.1, bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const WBCollectionsModal: React.FC<WBCollectionsModalProps> = ({ onClose, onImported }) => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState<WbVideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [importingUrl, setImportingUrl] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ url: string; msg: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await apiClient.getWbCollectionVideos();
      if (result.data) {
        setVideos(result.data.videos);
      } else if (result.error) {
        setLoadError(result.error.message);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Error de conexión');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (v: WbVideoItem) => {
    if (v.alreadyImported) {
      setFeedback({ url: v.url, msg: t('wb_import.already_imported_msg'), ok: false });
      return;
    }
    setImportingUrl(v.url);
    setFeedback(null);
    const result = await apiClient.importFromWhiteboard(v);
    setImportingUrl(null);
    if (result.data?.video) {
      setVideos((prev) => prev.map((x) => x.url === v.url ? { ...x, alreadyImported: true } : x));
      setFeedback({ url: v.url, msg: t('wb_import.import_ok'), ok: true });
      onImported(result.data.video);
    } else if (result.error?.message === 'already_imported') {
      setVideos((prev) => prev.map((x) => x.url === v.url ? { ...x, alreadyImported: true } : x));
      setFeedback({ url: v.url, msg: t('wb_import.already_imported_msg'), ok: false });
    } else {
      setFeedback({ url: v.url, msg: t('wb_import.import_fail'), ok: false });
    }
  };

  return (
    <BodyPortal>
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        isolation: 'isolate',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--card-bg, #ffffff)',
          color: 'var(--text-primary, #111827)',
          borderRadius: 16,
          width: 'min(92vw, 680px)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          transition: 'background 0.3s ease, color 0.3s ease',
          position: 'relative',
          zIndex: 1001,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px 14px',
          borderBottom: '1px solid var(--border-color, var(--theme-border, #e5e7eb))',
          background: 'var(--card-bg, #ffffff)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              background: 'var(--theme-primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)',
              borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            }}>WB</span>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--card-title-color, var(--text-primary, #111827))' }}>
              {t('wb_import.modal_title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close', 'Close')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-primary, #111827)', display: 'flex', alignItems: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: 'var(--card-bg, #ffffff)' }}>
          {loading ? (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary, #6b7280)', marginTop: 40 }}>
              {t('wb_import.loading')}
            </p>
          ) : loadError ? (
            <div style={{ textAlign: 'center', marginTop: 40 }}>
              <p style={{ color: 'var(--text-secondary, #6b7280)', marginBottom: 12 }}>
                {t('wb_import.connect_error', 'Could not connect to Whiteboard Collections.')}
              </p>
              <code style={{
                display: 'block', fontSize: 11, background: 'var(--controlsBg, #f3f4f6)',
                color: 'var(--controlsForeground, #374151)', padding: '8px 12px',
                borderRadius: 8, wordBreak: 'break-all', marginBottom: 14,
              }}>{loadError}</code>
              <button
                type="button"
                onClick={load}
                style={{
                  padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: 'var(--theme-primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)',
                  border: 'none', cursor: 'pointer',
                }}
              >{t('common.retry', 'Retry')}</button>
            </div>
          ) : videos.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary, #6b7280)', marginTop: 40 }}>
              {t('wb_import.empty')}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {videos.map((v) => (
                <div
                  key={v.url}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: 'var(--bg-alternate, #f3f4f6)',
                    border: '1px solid var(--border-color, var(--theme-border, #e5e7eb))',
                    borderRadius: 10, padding: '10px 14px',
                    transition: 'background 0.2s ease',
                  }}
                >
                  {/* Thumbnail from blob URL */}
                  <video
                    src={v.url}
                    style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 6, flexShrink: 0, background: '#000' }}
                    preload="metadata"
                    muted
                    aria-hidden="true"
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0, fontWeight: 600, fontSize: 13,
                      color: 'var(--card-title-color, var(--text-primary, #111827))',
                      overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                    }}>
                      {wbDisplayName(v.name, v.createdAt, t('wb_import.default_title', 'Whiteboard video'))}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>
                      {formatDate(v.createdAt)}{formatWbSize(v.fileSize)}
                    </p>
                    {feedback?.url === v.url && (
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: feedback.ok ? 'var(--theme-primary, #4f46e5)' : 'var(--text-secondary, #6b7280)' }}>
                        {feedback.msg}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={v.alreadyImported || importingUrl === v.url}
                    onClick={() => handleImport(v)}
                    aria-label={v.alreadyImported ? t('wb_import.already_imported') : t('wb_import.import')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      border: 'none', cursor: v.alreadyImported ? 'default' : 'pointer',
                      background: v.alreadyImported ? 'transparent' : 'var(--theme-primary, #4f46e5)',
                      color: v.alreadyImported ? 'var(--text-secondary, #6b7280)' : '#fff',
                      flexShrink: 0, transition: 'background 0.2s',
                    }}
                  >
                    {v.alreadyImported ? (
                      <><CheckCircle2 size={14} aria-hidden /> {t('wb_import.already_imported')}</>
                    ) : importingUrl === v.url ? (
                      '…'
                    ) : (
                      <><Download size={14} aria-hidden /> {t('wb_import.import')}</>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </BodyPortal>
  );
};

export default WBCollectionsModal;
